import { useEffect, useMemo, useRef, useState } from 'react';

import dayjs from 'dayjs';

import type { MetagameBingoEvent } from '../../stores/bingo/types';
import { useStores } from '../../stores/useStores';

const CC_DISPLAY_DURATION_MS = 4000;
const CASH_DISPLAY_DURATION_MS = 6000;

interface UseEventRotationResult {
  displayEvent: MetagameBingoEvent | null;
  shouldRotate: boolean;
  upcomingEvents: MetagameBingoEvent[];
}

export const useEventRotation = (): UseEventRotationResult => {
  const { bingoStore, subscriptionStore } = useStores();
  const {
    hasEverParticipatedInBingo,
    nextCasualBingoEvent,
    nextSweepBingoEvent,
    isLiveBingo,
    hasBingoResultToShow,
    bingoResultToShow,
    currentBingoEvent,
  } = bingoStore;
  const isFirstTimePlayer = !subscriptionStore.finishedCrownPrimeWelcomeTutorial;

  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [displayEvent, setDisplayEvent] = useState<MetagameBingoEvent | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const rotationCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isWithinOneMinuteOfNextEvent, setIsWithinOneMinuteOfNextEvent] = useState(false);

  const upcomingEvents = useMemo(() => {
    const events: MetagameBingoEvent[] = [];

    if (nextCasualBingoEvent && !nextCasualBingoEvent.isPlaceholder) {
      const isUpcoming = dayjs(nextCasualBingoEvent.startDate).isAfter(dayjs());
      const isNotCurrentLive = !isLiveBingo || bingoStore.currentBingoEvent?.id !== nextCasualBingoEvent.id;
      if (isUpcoming && isNotCurrentLive) {
        events.push(nextCasualBingoEvent);
      }
    }

    if (nextSweepBingoEvent && !nextSweepBingoEvent.isPlaceholder) {
      const isUpcoming = dayjs(nextSweepBingoEvent.startDate).isAfter(dayjs());
      const isNotCurrentLive = !isLiveBingo || bingoStore.currentBingoEvent?.id !== nextSweepBingoEvent.id;
      if (isUpcoming && isNotCurrentLive) {
        events.push(nextSweepBingoEvent);
      }
    }

    return events.sort((a, b) => dayjs(a.startDate).diff(dayjs(b.startDate)));
  }, [nextCasualBingoEvent, nextSweepBingoEvent, isLiveBingo, bingoStore.currentBingoEvent]);

  const shouldRotate = useMemo(() => {
    if (isFirstTimePlayer) return false;

    if (!hasEverParticipatedInBingo) return false;

    if (isLiveBingo) return false;

    if (hasBingoResultToShow && bingoResultToShow?.totalUserReward) return false;

    if (currentBingoEvent && bingoStore.isEventAboutToStart(currentBingoEvent)) return false;

    if (upcomingEvents.length < 2) return false;

    if (isWithinOneMinuteOfNextEvent) return false;

    const nextEvent = upcomingEvents[0];
    if (nextEvent) {
      const now = dayjs();
      const msToStart = dayjs(nextEvent.startDate).diff(now, 'millisecond');
      const ONE_MINUTE_MS = 60000;
      if (msToStart <= ONE_MINUTE_MS) {
        return false;
      }
    }

    return true;
  }, [
    isFirstTimePlayer,
    hasEverParticipatedInBingo,
    isLiveBingo,
    hasBingoResultToShow,
    bingoResultToShow?.totalUserReward,
    currentBingoEvent,
    bingoStore,
    upcomingEvents,
    isWithinOneMinuteOfNextEvent,
  ]);

  useEffect(() => {
    if (rotationCheckTimeoutRef.current) {
      clearTimeout(rotationCheckTimeoutRef.current);
      rotationCheckTimeoutRef.current = null;
    }

    setIsWithinOneMinuteOfNextEvent(false);

    if (upcomingEvents.length >= 2 && !isLiveBingo && !isFirstTimePlayer) {
      const nextEvent = upcomingEvents[0];
      if (nextEvent) {
        const now = dayjs();
        const msToStart = dayjs(nextEvent.startDate).diff(now, 'millisecond');
        const FIVE_MINUTE_MS = 5 * 60000;

        if (msToStart > FIVE_MINUTE_MS) {
          const msUntilFiveMinutesBefore = msToStart - FIVE_MINUTE_MS;
          rotationCheckTimeoutRef.current = setTimeout(() => {
            setIsWithinOneMinuteOfNextEvent(true);
          }, msUntilFiveMinutesBefore);
        } else {
          setIsWithinOneMinuteOfNextEvent(true);
        }
      }
    }

    return () => {
      if (rotationCheckTimeoutRef.current) {
        clearTimeout(rotationCheckTimeoutRef.current);
      }
    };
  }, [upcomingEvents, isLiveBingo, isFirstTimePlayer]);

  useEffect(() => {
    if (upcomingEvents.length > 0 && !displayEvent) {
      setDisplayEvent(upcomingEvents[0]);
    }
  }, [upcomingEvents, displayEvent]);

  useEffect(() => {
    if (!shouldRotate) {
      setCurrentEventIndex(0);
      setDisplayEvent(null);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    const currentEvent = displayEvent || upcomingEvents[currentEventIndex];
    const duration = currentEvent?.coinType === 'CASH' ? CASH_DISPLAY_DURATION_MS : CC_DISPLAY_DURATION_MS;

    timeoutRef.current = setTimeout(() => {
      const nextIndex = (currentEventIndex + 1) % upcomingEvents.length;
      setCurrentEventIndex(nextIndex);
      setDisplayEvent(upcomingEvents[nextIndex]);
    }, duration);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [shouldRotate, upcomingEvents, currentEventIndex, displayEvent]);

  return {
    displayEvent,
    shouldRotate,
    upcomingEvents,
  };
};
